import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Loader2, ImageOff, ArrowUpDown, BookOpen, Film, Download, Search, Flag, X, Eye, Check, MessageCircle, Share2, Sparkles, Plus, Mic, Heart, HelpCircle } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { saveOrShareVideo } from "@/lib/saveVideo";
import { VIDEO_LANGS } from "@/lib/languages";
import StarRating from "@/components/StarRating";
import CreatorUnlockModal from "@/components/CreatorUnlockModal";
import WhatIsModal from "@/components/WhatIsModal";
import BrandName from "@/components/BrandName";
import MobileCreateButton from "@/components/MobileCreateButton";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const formatViews = (n) => {
  n = n || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${n}`;
};

const REPORT_REASONS = [
  { id: "inappropriate", label: "Inappropriate or offensive" },
  { id: "dangerous", label: "Dangerous or unsafe" },
  { id: "spam", label: "Spam or misleading" },
  { id: "copyright", label: "Copyright / not theirs to post" },
  { id: "other", label: "Something else" },
];

const STYLE_META = {
  howto: { e: "📋", l: "How-To" },
  lecture: { e: "🎓", l: "Lecture" },
  documentary: { e: "🎬", l: "Documentary" },
  storytelling: { e: "📖", l: "Story" },
  coach: { e: "🔥", l: "Coach" },
  casual: { e: "☕", l: "Casual" },
  brief: { e: "💼", l: "Brief" },
  showhost: { e: "🎤", l: "Show" },
  advice: { e: "🧭", l: "Advice" },
  news: { e: "📰", l: "News" },
};


export default function Community({ mode = "videos" }) {
  const isPodcast = mode === "podcasts";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unlockOpen, setUnlockOpen] = useState(false);

  const [categories, setCategories] = useState([]);
  const [styles, setStyles] = useState([]);
  const [channels, setChannels] = useState([]);
  const [category, setCategory] = useState("");
  const [style, setStyle] = useState("");
  const [channel, setChannel] = useState("");
  const [language, setLanguage] = useState("");
  const [level, setLevel] = useState("");
  const savedPrefs = useRef((() => { try { return JSON.parse(localStorage.getItem("vy_feed_prefs") || "{}"); } catch { return {}; } })()).current;
  const [sort, setSort] = useState(savedPrefs.sort || "fresh");
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Orientation toggles — default: Landscape ON, Portrait OFF.
  const [orientLandscape, setOrientLandscape] = useState(savedPrefs.landscape ?? true);
  const [orientPortrait, setOrientPortrait] = useState(savedPrefs.portrait ?? false);
  // Remember the viewer's sort + Landscape/Portrait choice for their next visit.
  useEffect(() => {
    try { localStorage.setItem("vy_feed_prefs", JSON.stringify({ sort, landscape: orientLandscape, portrait: orientPortrait })); } catch { /* noop */ }
  }, [sort, orientLandscape, orientPortrait]);
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("inappropriate");
  const [reportBusy, setReportBusy] = useState(false);
  const [loveState, setLoveState] = useState({});
  const toggleLove = async (e, m) => {
    e.stopPropagation();
    const prev = loveState[m.id] || { loved: false, count: m.loves || 0 };
    const optimistic = { loved: !prev.loved, count: prev.count + (prev.loved ? -1 : 1) };
    setLoveState((s) => ({ ...s, [m.id]: optimistic }));
    try { const { data } = await api.post(`/community/${m.id}/love`); setLoveState((s) => ({ ...s, [m.id]: { loved: data.loved, count: data.count } })); }
    catch { setLoveState((s) => ({ ...s, [m.id]: prev })); }
  };
  const LoveHeart = ({ m, testid }) => {
    const st = loveState[m.id] || { loved: false, count: m.loves || 0 };
    return (
      <button data-testid={testid} onClick={(e) => toggleLove(e, m)}
        className="flex items-center gap-1 hover:opacity-80 transition-opacity active:scale-95"
        title="Send some love">
        <Heart size={16} className={st.loved ? "text-[#e11d48]" : "text-[#9a96ac]"} fill={st.loved ? "#e11d48" : "none"} />
        <span className={`text-xs font-extrabold tabular-nums ${st.loved ? "text-[#e11d48]" : "text-[#9a96ac]"}`}>{st.count}</span>
      </button>
    );
  };
  const [showIntro, setShowIntro] = useState(false);
  // Fresh a new shuffle seed each time the page opens, so the default feed order changes per visit.
  const seedRef = useRef(Math.floor(Math.random() * 1e9));


  // Swipe navigation between pages (touch + the animated Swipe button).
  const canNext = !!(data && page < data.pages);

  useEffect(() => {
    api.get("/community/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/community/styles").then(({ data }) => setStyles(data || [])).catch(() => {});
    api.get("/community/channels").then(({ data }) => setChannels(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const reqIdRef = useRef(0);
  const load = useCallback(() => {
    const rid = ++reqIdRef.current;  // only the newest request may update the feed (fast toggles race)
    if (page === 1) setLoading(true); else setLoadingMore(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (style) params.set("style", style);
    if (channel) params.set("channel", channel);
    if (language) params.set("language", language);
    if (level) params.set("level", level);
    if (isPodcast) params.set("content", "podcast");
    params.set("orientation", [orientLandscape && "landscape", orientPortrait && "portrait"].filter(Boolean).join(","));
    params.set("sort", sort);
    if (sort === "fresh") params.set("seed", seedRef.current);
    params.set("page", page);
    if (search) params.set("search", search);
    api.get(`/community?${params.toString()}`)
      .then(({ data: d }) => rid === reqIdRef.current && setData((prev) => {
        if (page === 1 || !prev) return d;
        const seen = new Set(prev.items.map((x) => x.id));
        return { ...d, items: [...prev.items, ...d.items.filter((x) => !seen.has(x.id))] };
      }))
      .catch(() => { if (rid === reqIdRef.current && page === 1) setData({ items: [], total: 0, page: 1, pages: 1 }); })
      .finally(() => { if (rid === reqIdRef.current) { setLoading(false); setLoadingMore(false); } });
  }, [category, style, channel, language, level, sort, page, search, isPodcast, orientLandscape, orientPortrait]);

  useEffect(() => { load(); }, [load]);

  // Infinite scroll: fetch the next page when the sentinel below the grid comes into view.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !canNext || loadingMore || loading) return;
    const io = new IntersectionObserver((es) => { if (es[0].isIntersecting) setPage((p) => p + 1); }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [canNext, loadingMore, loading, data]);

  // Until real channels exist, show placeholder labels so the menu demonstrates the concept.
  const channelOptions = channels.length ? channels : ["Channel 1", "Channel 2", "Channel 3"];

  const styleLabel = styles.find((s) => s.id === style)?.label || "";
  const hasFilter = !!(search || style || category || channel || language);

  // Empty-state "gap → create": prefill the Studio with the current search + style so a creator
  // can fill the missing spot with one tap. Stash survives a login redirect that strips the query.
  const startCreate = () => {
    if (isPodcast) { navigate("/podcast"); return; }
    const params = new URLSearchParams();
    if (search) params.set("goal", search);
    if (style) params.set("style", style);
    if (category) params.set("category", category);
    try {
      localStorage.setItem("sms_studio_prefill", JSON.stringify({ goal: search || "", style: style || "", category: category || "" }));
    } catch { /* ignore */ }
    navigate(`/studio${params.toString() ? `?${params.toString()}` : ""}`);
  };

  // Avatar click: go to the creator's page if unlocked; if it's YOUR own avatar but not yet
  // unlocked, show the "X of 10 videos" progress lightbox.
  const onAvatarClick = (e, m) => {
    e.stopPropagation();
    if (m.creator_has_page && m.creator_username) {
      navigate(`/creator/${m.creator_username}`);
    } else if (user && m.creator_username && user.username === m.creator_username) {
      setUnlockOpen(true);
    } else {
      toast("This creator hasn't unlocked a page yet.", { icon: "🎬" });
    }
  };

  const downloadVideo = async (e, m) => {
    e.stopPropagation();
    setDl(m.id);
    try {
      const res = await api.get(`/community/${m.id}/video/download`, { responseType: "blob" });
      const filename = `stepmystep-${(m.title || "manual").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 50)}.mp4`;
      await saveOrShareVideo(res.data, filename, m.title || "Vittyyo video");
    } catch {
      toast.error("Couldn't download the video");
    } finally {
      setDl(null);
    }
  };

  const shareCard = async (e, m) => {
    e.stopPropagation();
    const url = `${window.location.origin}/api/v/${m.id}`;
    const title = m.title || "Vittyyo video";
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
    } catch (err) { if (err?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere!"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  const submitReport = async () => {
    if (!reportFor) return;
    setReportBusy(true);
    try {
      const { data: res } = await api.post(`/community/${reportFor.id}/report`, { reason: reportReason });
      toast.success(res.auto_hidden
        ? "Thanks — this post has been hidden for review."
        : "Thanks for the report. Our team will review it.");
      setReportFor(null);
      if (res.auto_hidden) load();
    } catch {
      toast.error("Couldn't submit the report");
    } finally {
      setReportBusy(false);
    }
  };

  return (
    <div style={{ background: "#000", minHeight: "calc(100vh - 64px)" }} data-testid="community-page">
      <div className="max-w-7xl mx-auto px-4 pt-3 pb-16">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2 min-w-0 text-white" data-testid="community-title">
          <BrandName dark /> <span className="whitespace-nowrap sms-outline-dark">{isPodcast ? "PODCASTS" : "VIDEOS"}</span>
        </h1>
        {(() => {
          const orientPill = (on, setOn, label, testid, extra = "") => (
            <button
              data-testid={testid}
              aria-pressed={on}
              onClick={() => { setOn((v) => !v); setPage(1); }}
              className={`text-sm font-bold rounded-full px-4 py-2 border-2 transition-[opacity,color,border-color] whitespace-nowrap ${extra}`}
              style={on ? { background: "#000", color: "#fff", borderColor: "#5d8a52" } : { background: "#000", color: "#8a8a8a", borderColor: "#3a3a3a", opacity: 0.55 }}
              title={`${label} videos ${on ? "showing — tap to hide" : "hidden — tap to show"}`}
            >
              {label}
            </button>
          );
          return (
            <div className="flex items-center gap-2 shrink-0">
              <div className="hidden sm:flex items-center gap-2">
                {orientPill(orientLandscape, setOrientLandscape, "Landscape", "community-orient-landscape")}
                {orientPill(orientPortrait, setOrientPortrait, "Portrait", "community-orient-portrait")}
              </div>
              <MobileCreateButton />
              <button
                data-testid="community-get-help"
                onClick={() => setShowIntro(true)}
                title="How vittyyo works"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold rounded-full px-3 sm:px-4 py-2 border-2 border-[#5d8a52] bg-black text-white whitespace-nowrap transition-transform hover:scale-[1.04]"
              >
                <HelpCircle size={16} className="text-[#ffd21e]" /> Get Help
              </button>
            </div>
          );
        })()}
      </div>

      {/* Filter bar: Newest · Category · Channel · Language + search */}
      <div className="grid grid-cols-6 gap-2 items-start sm:flex sm:flex-wrap sm:items-center">
        <select
          data-testid="community-level"
          value={level}
          onChange={(e) => { setLevel(e.target.value); setPage(1); }}
          className="community-pill col-span-2 w-full sm:w-auto text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer focus:border-[#16307f] transition-colors"
        >
          <option value="">Level (All)</option>
          <option value="adult">Adult</option>
          <option value="teens">Teens</option>
          <option value="kids">Kids</option>
        </select>

        <select
          data-testid="community-sort"
          value={sort}
          onChange={(e) => { if (e.target.value === "fresh") seedRef.current = Math.floor(Math.random() * 1e9); setSort(e.target.value); setPage(1); }}
          className="community-pill col-span-2 w-full sm:w-auto text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer focus:border-[#16307f] transition-colors"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="views">Most Viewed</option>
          <option value="rating">Top Rated</option>
          <option value="loved">Most Loved</option>
          <option value="following">Following</option>
          <option value="bestselling">Best Selling</option>
          <option value="az">A–Z</option>
          <option value="fresh">🔀 Random</option>
        </select>

        {/* Mobile only: Landscape sits at the end of row 1 (Level · Newest · Landscape) */}
        <button
          data-testid="community-orient-landscape-m"
          aria-pressed={orientLandscape}
          onClick={() => { setOrientLandscape((v) => !v); setPage(1); }}
          className="sm:hidden col-span-2 w-full text-sm font-bold rounded-full px-4 py-2 border-2 transition-[opacity,color,border-color]"
          style={orientLandscape ? { background: "#000", color: "#fff", borderColor: "#5d8a52" } : { background: "#000", color: "#8a8a8a", borderColor: "#3a3a3a", opacity: 0.55 }}
        >
          Landscape
        </button>

        <select
          data-testid="community-category"
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="community-pill col-span-4 w-full sm:w-auto text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer focus:border-[#16307f] transition-colors"
        >
          <option value="">Category (All)</option>
          {[...categories].sort((a, b) => a.localeCompare(b)).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        {/* Mobile only: Portrait sits at the end of row 2 (Category · Portrait) */}
        <button
          data-testid="community-orient-portrait-m"
          aria-pressed={orientPortrait}
          onClick={() => { setOrientPortrait((v) => !v); setPage(1); }}
          className="sm:hidden col-span-2 w-full text-sm font-bold rounded-full px-4 py-2 border-2 transition-[opacity,color,border-color]"
          style={orientPortrait ? { background: "#000", color: "#fff", borderColor: "#5d8a52" } : { background: "#000", color: "#8a8a8a", borderColor: "#3a3a3a", opacity: 0.55 }}
        >
          Portrait
        </button>

        <select
          data-testid="community-style"
          value={style}
          onChange={(e) => { setStyle(e.target.value); setPage(1); }}
          className={`community-pill col-span-4 w-full sm:w-auto text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer focus:border-[#16307f] transition-colors ${isPodcast ? "hidden" : ""}`}
        >
          <option value="">Speaking Style (All)</option>
          {styles.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>

        {/* Language sits at the end of row 3 (Speaking Style · Language) */}
        <select
          data-testid="community-language"
          value={language}
          onChange={(e) => { setLanguage(e.target.value); setPage(1); }}
          className="community-pill col-span-2 w-full sm:w-auto text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2 outline-none cursor-pointer focus:border-[#16307f] transition-colors"
        >
          <option value="">Language</option>
          {Object.entries(VIDEO_LANGS).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>

        <div className="relative col-span-4 w-full sm:flex-1 sm:min-w-[180px] sm:w-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/80" />
          <input
            data-testid="community-search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={isPodcast ? "Search Podcasts" : "Search Videos"}
            className="w-full text-sm text-white placeholder:text-white/70 bg-black border-2 border-[#5d8a52] rounded-full pl-9 pr-8 py-2 outline-none focus:border-[#16307f] transition-colors"
          />
          {searchInput && (
            <button
              data-testid="community-search-clear"
              onClick={() => setSearchInput("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          data-testid="community-get-help-m"
          onClick={() => setShowIntro(true)}
          title="How vittyyo works"
          className="sm:hidden col-span-2 w-full inline-flex items-center justify-center gap-1 text-sm font-bold rounded-full px-2 py-2 border-2 border-[#5d8a52] bg-black text-white whitespace-nowrap"
        >
          <HelpCircle size={15} className="text-[#ffd21e]" /> Get Help
        </button>
      </div>

      <div className="mt-4 sm:mt-6">
      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>
      ) : !data || data.items.length === 0 ? (
        <div className="sms-card p-10 sm:p-12 text-center max-w-xl mx-auto" data-testid="community-empty-state" style={{ borderColor: "#5d8a52" }}>
          <div className="w-14 h-14 rounded-full bg-[#eef3ea] flex items-center justify-center mx-auto mb-4">
            <Sparkles size={26} className="text-[#4f7746]" />
          </div>
          {sort === "following" ? (
            <>
              <h3 className="text-lg font-bold text-[#171426]">
                {data?.needs_login ? "Sign in to see who you follow" : "You're not following anyone yet"}
              </h3>
              <p className="text-sm text-[#6b6880] mt-2">
                {data?.needs_login
                  ? "Log in, then tap Follow on a creator's page to build your personal feed."
                  : "Open a creator's page and tap Follow — their latest videos will show up here."}
              </p>
              <button
                data-testid="following-empty-browse"
                onClick={() => { setSort("newest"); setPage(1); }}
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: "#4f7746" }}
              >
                Browse all videos
              </button>
            </>
          ) : hasFilter ? (
            <>
              <h3 className="text-lg font-bold text-[#171426]">
                No {styleLabel ? <span className="text-[#4f7746]">{styleLabel}</span> : ""} videos
                {search ? <> about “<span className="text-[#4f7746]">{search}</span>”</> : ""}
                {category ? <> in <span className="text-[#4f7746]">{category}</span></> : ""} yet
              </h3>
              <p className="text-sm text-[#6b6880] mt-2">
                That's a gap in the library waiting to be filled — be the first to create it.
              </p>
              <button
                data-testid="empty-create-btn"
                onClick={startCreate}
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: "#4f7746" }}
              >
                <Plus size={16} /> Create this{styleLabel ? ` ${styleLabel}` : ""}
              </button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-bold text-[#171426]" data-testid="community-empty-title">{!orientLandscape && !orientPortrait ? "Both views are off" : "Nothing here yet"}</h3>
              <p className="text-sm text-[#6b6880] mt-2">{!orientLandscape && !orientPortrait
                ? "Turn on Landscape or Portrait to see videos."
                : !orientLandscape ? "No portrait videos yet — turn Landscape back on, or be the first to post one."
                : `Be the first to add ${isPodcast ? "a podcast" : "a video"} to the library.`}</p>
              <button
                data-testid="empty-create-btn"
                onClick={startCreate}
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-white px-5 py-2.5 rounded-full transition-transform hover:scale-105"
                style={{ background: "#4f7746" }}
              >
                <Plus size={16} /> Create {isPodcast ? "a podcast" : "a video"}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-2.5 sm:gap-3">
            {data.items.map((m, i) => (
              <div
                key={m.id}
                data-testid={`community-card-${m.id}`}
                onClick={() => navigate(`/community/${m.id}`)}
                className="sms-card sms-dark-page community-pop overflow-hidden text-left group sms-fade-up cursor-pointer"
                style={{ animationDelay: `${i * 0.03}s` }}
              >
                <div className="w-full aspect-video bg-[#100d1c] overflow-hidden relative">
                  {m.hero_image_id ? (
                    <img src={imageUrl(m.hero_image_id)} alt={m.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#b7b3c8]"><ImageOff size={26} /></div>
                  )}
                  {/* Board cards stay clean — the PODCAST tag sits in the meta row below the image. */}
                  {(m.did_it_count || 0) > 0 && (
                    <span data-testid={`community-didit-${m.id}`} className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-extrabold text-[#0f0f16] bg-[#8fbf82] px-2 py-0.5 rounded-full shadow">
                      <Check size={11} strokeWidth={3} /> {m.did_it_count} did it
                    </span>
                  )}
                  <button
                    data-testid={`community-report-${m.id}`}
                    onClick={(e) => { e.stopPropagation(); setReportReason("inappropriate"); setReportFor(m); }}
                    title="Report this post"
                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-black/45 text-white/90 hover:bg-black/70 hover:text-white transition-colors"
                  >
                    <Flag size={13} />
                  </button>
                </div>
                <div className="p-2.5">
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#ffd21e] shrink-0">{m.category}</span>
                      {m.comment_count > 0 && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold text-[#5d8a52] shrink-0" data-testid={`community-comments-count-${m.id}`}>
                          <MessageCircle size={12} /> {m.comment_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {(isPodcast || m.format === "podcast") && (
                        <span
                          data-testid={`podcast-badge-${m.id}`}
                          className="flex items-center gap-1 text-[10px] font-extrabold text-white bg-[#1e3aaa] px-2 py-0.5 rounded-full shrink-0"
                        >
                          <Mic size={11} /> PODCAST
                        </span>
                      )}
                      {!isPodcast && m.format !== "podcast" && STYLE_META[m.style] && (
                        <span
                          data-testid={`community-style-badge-${m.id}`}
                          className="flex items-center gap-1 text-[10px] font-extrabold text-[#3f6238] bg-[#eef3ea] px-2 py-0.5 rounded-full shrink-0"
                        >
                          <span className="uppercase tracking-wide">{STYLE_META[m.style].l}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-1.5 mt-0.5">
                    <div className="font-bold text-[13px] leading-snug line-clamp-2 min-h-[2.4em] flex-1">{m.title}</div>
                    <button
                      data-testid={`community-share-${m.id}`}
                      onClick={(e) => shareCard(e, m)}
                      title="Share this video"
                      aria-label="Share this video"
                      className="shrink-0 -mr-0.5 -mt-0.5 p-1 text-[#171426] hover:text-[#4f7746] transition-colors"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    <StarRating value={m.rating_avg} count={m.rating_count} size={13} />
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-[#9a96ac] shrink-0" data-testid={`community-views-${m.id}`}>
                      <Eye size={12} /> {formatViews(m.views)}
                    </span>
                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                      <LoveHeart m={m} testid={`community-love-${m.id}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div ref={sentinelRef} className="h-px" data-testid="community-scroll-sentinel" />
          {loadingMore && <div className="py-8 flex justify-center" data-testid="community-loading-more"><Loader2 size={24} className="spin text-[#5d8a52]" /></div>}
          {!canNext && data.pages > 1 && <p className="text-center text-xs font-semibold text-white/40 py-8" data-testid="community-end">You're all caught up</p>}
        </>
      )}
      </div>

      {reportFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#171426]/50 backdrop-blur-sm p-4"
          onClick={() => setReportFor(null)}
          data-testid="report-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-sm rounded-3xl p-6 sms-fade-up"
            data-testid="report-modal"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-extrabold flex items-center gap-2"><Flag size={18} className="text-[#e11d48]" /> Report post</h2>
              <button onClick={() => setReportFor(null)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#6b6880] mb-4 line-clamp-2">"{reportFor.title}"</p>
            <div className="space-y-2 mb-5">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r.id}
                  data-testid={`report-reason-${r.id}`}
                  onClick={() => setReportReason(r.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-left transition-colors"
                  style={reportReason === r.id
                    ? { background: "#fef2f2", color: "#e11d48", boxShadow: "inset 0 0 0 1.5px #fecdd3" }
                    : { background: "#eef1ea", color: "#4a475c" }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              data-testid="report-submit-btn"
              onClick={submitReport}
              disabled={reportBusy}
              className="w-full text-white font-bold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-70 transition-colors"
              style={{ background: "#e11d48" }}
            >
              {reportBusy && <Loader2 size={16} className="spin" />} Submit report
            </button>
          </div>
        </div>
      )}
      </div>
      <CreatorUnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} />
      <WhatIsModal open={showIntro} onClose={() => setShowIntro(false)} />
    </div>
  );
}
